import {
  CAMPAIGN_SYSTEM_USER_MESSAGE_PREFIX,
  TOOL_ERROR_PREFIX,
} from './systemChatMessages';
import {PARAMETERIZATION_SYSTEM_USER_MESSAGE_PREFIXES} from './parameterizationMessages';

const CAMPAIGN_TYPE_LABELS = {
  PRODUCT: 'Escolhi campanha de produto.',
  SERVICE: 'Escolhi campanha de serviço.',
  NONE: 'Não vou divulgar produto nem serviço específico nesta campanha.',
};

const FINAL_ACTION_LABELS = {
  SELECT_PLAN: 'Quero escolher um plano.',
  NEW_CAMPAIGN: 'Quero criar uma nova campanha.',
  FINISH: 'Finalizar campanha.',
};

function parseCampaignTypeBody(body) {
  if (!body.startsWith('tipo_campanha ')) {
    return null;
  }
  const value = body.slice('tipo_campanha '.length).trim().toUpperCase();
  if (!CAMPAIGN_TYPE_LABELS[value]) {
    return null;
  }
  return {
    visible: true,
    textKey: `campaignChat.userDecision.campaignType.${value}`,
    defaultText: CAMPAIGN_TYPE_LABELS[value],
    values: {type: value},
    imageUrls: [],
  };
}

function parseApprovalAction(raw) {
  const upper = raw?.toUpperCase();
  if (upper === 'APPROVED' || upper === 'EDIT' || upper === 'REJECT') {
    return upper;
  }
  return null;
}

function parseCampaignWidgetBody(body) {
  if (body.startsWith('quantidade_artes ')) {
    const value = parseInt(body.split(/\s+/)[1] || '', 10);
    if (Number.isNaN(value) || value <= 0) {
      return null;
    }
    return {
      visible: true,
      textKey: 'campaignChat.userDecision.postQuantity',
      defaultText: `Quero ${value} artes na campanha.`,
      values: {quantity: value},
      imageUrls: [],
    };
  }

  if (body.startsWith('imagens_campanha ')) {
    const jsonPart = body.slice('imagens_campanha '.length);
    try {
      const imageUrls = JSON.parse(jsonPart);
      if (!Array.isArray(imageUrls)) {
        return null;
      }
      return {
        visible: true,
        textKey:
          imageUrls.length > 0
            ? 'campaignChat.userDecision.imagesSent'
            : 'campaignChat.userDecision.imagesSkipped',
        defaultText:
          imageUrls.length > 0
            ? `Enviei ${imageUrls.length} imagem(ns) para usar nas artes.`
            : 'Prefiro seguir sem enviar imagens minhas.',
        values: {count: imageUrls.length},
        imageUrls,
      };
    } catch {
      return null;
    }
  }

  if (body.startsWith('aprovacao_estrategia ')) {
    const action = parseApprovalAction(body.split(/\s+/)[1]);
    if (!action) {
      return null;
    }
    const labels = {
      APPROVED: 'Aprovo o documento de estratégia.',
      EDIT: 'Quero editar o documento de estratégia.',
      REJECT: 'Quero reprovar e regerar a estratégia.',
    };
    return {
      visible: true,
      textKey: `campaignChat.userDecision.strategyApproval.${action}`,
      defaultText: labels[action],
      values: {action},
      imageUrls: [],
    };
  }

  if (body.startsWith('aprovacao_artes ')) {
    const action = parseApprovalAction(body.split(/\s+/)[1]);
    if (!action) {
      return null;
    }
    const labels = {
      APPROVED: 'Aprovo as artes geradas.',
      EDIT: 'Quero editar as artes em lote.',
      REJECT: 'Quero reprovar e regerar as artes.',
    };
    return {
      visible: true,
      textKey: `campaignChat.userDecision.artsApproval.${action}`,
      defaultText: labels[action],
      values: {action},
      imageUrls: [],
    };
  }

  if (body.startsWith('modo_conclusao ')) {
    const mode = body.split(/\s+/)[1]?.toUpperCase();
    if (mode !== 'SCHEDULE' && mode !== 'FINALIZE') {
      return null;
    }
    return {
      visible: true,
      textKey: `campaignChat.userDecision.completionMode.${mode}`,
      defaultText:
        mode === 'SCHEDULE'
          ? 'Quero agendar as publicações.'
          : 'Quero finalizar a campanha sem agendar.',
      values: {mode},
      imageUrls: [],
    };
  }

  if (body.startsWith('redes_sociais ')) {
    const payload = body.slice('redes_sociais '.length).trim();
    let networks = [];

    if (payload.startsWith('[')) {
      try {
        const socialSelection = JSON.parse(payload);
        if (Array.isArray(socialSelection)) {
          networks = socialSelection
            .map((item) => item?.name)
            .filter(Boolean)
            .map((name) => String(name).trim().toLowerCase());
        }
      } catch {
        return null;
      }
    } else {
      networks = payload
        .split(',')
        .map((n) => n.trim())
        .filter(Boolean);
    }

    if (networks.length === 0) {
      return null;
    }

    return {
      visible: true,
      textKey: 'campaignChat.userDecision.socialNetworks',
      defaultText: `Redes selecionadas: ${networks.join(', ')}.`,
      values: {networks},
      imageUrls: [],
    };
  }

  if (body.startsWith('agendamento_configurado ')) {
    return {
      visible: true,
      textKey: 'campaignChat.userDecision.scheduleConfigured',
      defaultText: 'Configuração de agendamento confirmada.',
      values: {},
      imageUrls: [],
    };
  }

  if (body.startsWith('confirmar_edicao_lote ')) {
    const flag = body.split(/\s+/)[1]?.toUpperCase();
    if (flag !== 'CONFIRM' && flag !== 'CANCEL') {
      return null;
    }
    return {
      visible: true,
      textKey: `campaignChat.userDecision.batchEdit.${flag}`,
      defaultText:
        flag === 'CONFIRM'
          ? 'Confirmo a edição em lote das artes.'
          : 'Cancelo a edição em lote.',
      values: {confirmed: flag === 'CONFIRM'},
      imageUrls: [],
    };
  }

  if (body.startsWith('confirmar_rejeicao_lote ')) {
    const flag = body.split(/\s+/)[1]?.toUpperCase();
    if (flag !== 'CONFIRM' && flag !== 'CANCEL') {
      return null;
    }
    return {
      visible: true,
      textKey: `campaignChat.userDecision.batchReject.${flag}`,
      defaultText:
        flag === 'CONFIRM'
          ? 'Confirmo a reprovação e regeração das artes.'
          : 'Cancelo a reprovação.',
      values: {confirmed: flag === 'CONFIRM'},
      imageUrls: [],
    };
  }

  if (body.startsWith('acao_final ')) {
    const action = body.split(/\s+/)[1]?.toUpperCase();
    if (!FINAL_ACTION_LABELS[action]) {
      return null;
    }
    return {
      visible: true,
      textKey: `campaignChat.userDecision.finalAction.${action}`,
      defaultText: FINAL_ACTION_LABELS[action],
      values: {action},
      imageUrls: [],
    };
  }

  return null;
}

function parseParameterizationMessage(text) {
  const normalized = text.trim();

  if (normalized === 'PARAMETRIZACAO_INICIAR') {
    return null;
  }

  const prefix = PARAMETERIZATION_SYSTEM_USER_MESSAGE_PREFIXES.find((p) =>
    normalized.startsWith(p),
  );
  if (!prefix || prefix !== 'PARAMETRIZACAO_COMPLETA:') {
    return null;
  }

  const step = normalized.slice(prefix.length).trim().toLowerCase();
  const labels = {
    paleta: 'Pronto, escolhi a paleta de cores.',
    logo: 'Pronto, enviei o logo.',
    nicho: 'Pronto, escolhi o nicho.',
  };

  if (!labels[step]) {
    return null;
  }

  return {
    visible: true,
    textKey: `campaignChat.userDecision.parameterization.${step}`,
    defaultText: labels[step],
    values: {step},
    imageUrls: [],
  };
}

/**
 * Maps internal user protocol messages to friendly transcript presentation.
 * Unknown internal messages stay hidden (returns null).
 */
export function parseUserDecisionPresentation(text) {
  if (!text || typeof text !== 'string') {
    return null;
  }

  const normalized = text.trim();

  if (normalized.startsWith(TOOL_ERROR_PREFIX)) {
    return null;
  }

  const parameterization = parseParameterizationMessage(normalized);
  if (parameterization) {
    return parameterization;
  }

  if (!normalized.startsWith(CAMPAIGN_SYSTEM_USER_MESSAGE_PREFIX)) {
    return null;
  }

  const body = normalized.slice(CAMPAIGN_SYSTEM_USER_MESSAGE_PREFIX.length).trim();

  const campaignType = parseCampaignTypeBody(body);
  if (campaignType) {
    return campaignType;
  }

  return parseCampaignWidgetBody(body);
}
